'use client';

import { useState } from 'react';
import { LoginModal } from '../../_components/LoginModal';

/**
 * Full-screen auth gate for the account pages (Manage Bookings / Booking Detail / My Profile /
 * Change Password). When a logged-OUT user reaches a protected account page (e.g. clicking
 * "My Bookings" / "Profile" from the confirmation page), CMS pops the LOGIN modal rather than
 * bouncing to the home page.
 *
 * On successful login the modal reloads the page (`LoginModal` does `window.location.reload()`), so
 * the now-authenticated page renders its data. If the user CLOSES the modal we DON'T navigate away —
 * we show a "sign in to continue" fallback with a re-open button + a Home link. (Navigating on every
 * `onClose` is wrong: `LoginModal` also fires `onClose` on transient closes — unverified-account
 * login, signup, forgot-password — which would bounce the user mid-flow and break "Return to Login".)
 */
export function LoginGate({ onHome }: { onHome: () => void }) {
  const [open, setOpen] = useState(true);
  return (
    // `text-center` is scoped to the message/buttons `<div>` below, NOT this outer wrapper —
    // `LoginModal` is a SIBLING rendered inline here (no portal), so it fully inherits any
    // `text-align` set on an ancestor. Putting `text-center` up here centered the modal's own
    // Email/Password labels and inputs (the reported bug) whenever this gate fires — e.g. the
    // session expiring mid-browse on an account page (Manage Bookings / My Profile / Change
    // Password), which re-shows this gate and its `LoginModal`.
    <div className="bg-yellow-light min-h-screen flex flex-col items-center justify-center gap-4 py-20 px-4">
      <div className="text-center">
        <p className="text-secondary font-medium mb-4">Please sign in to view this page.</p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="px-5 py-2.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-secondary transition-colors"
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={onHome}
            className="px-5 py-2.5 rounded-full border border-border-soft text-secondary text-sm font-semibold hover:border-primary hover:text-primary transition-colors"
          >
            Go to home
          </button>
        </div>
      </div>
      <LoginModal isOpen={open} onClose={() => setOpen(false)} onReopen={() => setOpen(true)} />
    </div>
  );
}
